'use client'
import { useCallback, useState } from 'react'
import { createContainer } from 'unstated-next'

import {
  CreateFavoriteMutationVariables,
  CustomerProfileFavoritesNodeFragment,
  DeleteFavoriteMutationVariables,
} from '../../__generated__/graphql/catalog/graphql'
import {
  CREATE_FAVORITE_MUTATION,
  DELETE_FAVORITE_MUTATION,
} from '../../lib/graphql/mutations/FAVORITES_MUTATIONS'
import { GET_FAVORITES_QUERY } from '../../lib/graphql/queries/GET_FAVORITES'
import {
  RSCToastNotifications,
  ToastNotifications,
} from '../_components/dialogs/ToastNotification.client'
import { cookieKeys } from '../_config/Cookies.config'
import { useNextMutation } from '../_hooks/useNextMutation'
import { useNextQuery } from '../_hooks/useNextQuery'

import { useCookies } from './CookiesProvider.client'
import { useFeatureFlag } from './ExperimentationProvider.client'

const useFavoritesImpl = () => {
  const [cookies] = useCookies([cookieKeys.visitorId.key])
  const visitorIdCookie = cookies['figs-visitor-id']
  const [favorites, setFavorites] = useState<CustomerProfileFavoritesNodeFragment[]>([])

  const favoritesDecision = useFeatureFlag({
    key: 'favorites-flow-feature-flag',
    defaultVariant: 'favorites-control',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
  })

  // Whether or not the favorites functionality is considered enabled or not
  const enabled = favoritesDecision === 'favorites-variant'

  const { status } = useNextQuery(GET_FAVORITES_QUERY, {
    client: 'auth-supported',
    variables: { visitorId: visitorIdCookie?.valueOf() },
    skip: !enabled,
    onCompleted: data =>
      setFavorites(
        data.customerProfile?.favorites?.edges?.map(edge => edge?.node).filter(node => !!node) ?? []
      ),
  })

  const [addFavoriteMutation] = useNextMutation(CREATE_FAVORITE_MUTATION, {
    client: 'auth-supported',
  })
  const [removeFavoriteMutation] = useNextMutation(DELETE_FAVORITE_MUTATION, {
    client: 'auth-supported',
  })

  const addFavorite = useCallback(
    async ({ input, visitorId }: CreateFavoriteMutationVariables) => {
      if (!enabled) return { errors: null, data: null }

      try {
        const { errors, data } = await addFavoriteMutation({
          variables: { input, visitorId },
          optimisticResponse: vars => ({
            createCustomerProfileFavorite: {
              __typename: 'CustomerProfileFavorite',
              id: `${vars.input.productGroupHandle}-${vars.input.color}-${vars.input.fit}-${vars.input.size}`,
              productGroupHandle: input.productGroupHandle,
              fit: input.fit,
              color: input.color,
              size: input.size ?? null,
            },
          }),
          onCompleted: data => {
            setFavorites(f => [...f, data.createCustomerProfileFavorite])
          },
        })

        return { errors, data }
      } catch (error) {
        return { errors: [error], data: null }
      }
    },
    [addFavoriteMutation, enabled]
  )

  const removeFavorite = useCallback(
    async (input: DeleteFavoriteMutationVariables) => {
      if (!enabled) return
      await removeFavoriteMutation({
        variables: input,
        onCompleted: () => setFavorites(f => f.filter(({ id }) => id !== input.id)),
      })
    },
    [removeFavoriteMutation, enabled]
  )

  return {
    favorites,
    enabled,
    addFavorite,
    removeFavorite,
    status,
  }
}
const FavoritesContainer = createContainer(useFavoritesImpl)
export const FavoritesProvider = ({
  children,
  rscSupported = false,
}: {
  children: React.ReactNode
  rscSupported?: boolean
}) => {
  return (
    <FavoritesContainer.Provider>
      {rscSupported ? <RSCToastNotifications /> : <ToastNotifications />}
      {children}
    </FavoritesContainer.Provider>
  )
}

export const useFavorites = FavoritesContainer.useContainer
